import React from 'react';

import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import {
    Theme,
    AppBar,
    Toolbar,
    Link,
    Typography,
    Menu,
    MenuItem,
} from '@mui/material';
import { Menu as MenuIcon, Person } from '@mui/icons-material';

import { Link as RouterLink, useNavigate } from 'react-router-dom';

import { ADMIN_URL, API_URL } from 'config';
import { useAppSelector } from 'hooks';
import { AuthManager } from 'models';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            flexGrow: 1,
        },
        appBar: {
            zIndex: theme.zIndex.drawer + 1,
        },
        toolbar: {
            backgroundColor: theme.palette.background.nav,
        },
        menuText: {
            color: theme.palette.common.neutralDark,
        },
        right: {
            position: 'relative',
            marginLeft: 'auto',
            width: 'auto',
            marginRight: theme.spacing(2),
        },
        link: {
            color: theme.palette.common.black70,
            textDecoration: 'underline',
            display: 'inline-flex',
            marginLeft: theme.spacing(2),
            fontSize: 'larger',
            cursor: 'pointer',
        },
        navBtn: {
            display: 'inline-flex',
        },
        icon: {
            marginRight: theme.spacing(1),
        },
    }),
);

interface NavbarProps {
    style?: React.CSSProperties;
    className?: string;
}

const Navbar: React.FunctionComponent<NavbarProps> = ({ style, className }) => {
    const classes = useStyles();
    const navigate = useNavigate();

    const { auth } = useAppSelector((rootState) => rootState);

    const { tokens, user } = auth;

    let loginStatus = 'Not Logged In';
    if (user.status === 'loading' || tokens.status === 'loading') {
        loginStatus = 'Logging In...';
    } else if (user.status === 'finished' && user.object) {
        loginStatus = user.object.fullName;
    }

    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

    const handleMenuClick = (event: React.MouseEvent<any>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleMyDetailsClick = () => {
        handleMenuClose();
        navigate('/users/self');
    };

    const handleAdminPageClick = () => {
        handleMenuClose();
        window.open(ADMIN_URL, '_blank')?.focus();
    };

    const handleAPIPageClick = () => {
        handleMenuClose();
        window.open(API_URL, '_blank')?.focus();
    };

    const onLogoutClick = () => {
        handleMenuClose();
        AuthManager.logout();
    };

    return (
        <div className={`${classes.root} ${className ?? ''}`} style={style}>
            <AppBar position="sticky" className={classes.appBar}>
                <Toolbar className={classes.toolbar}>
                    <Link component={RouterLink} to="/">
                        <img
                            aria-label="Navigate to menu"
                            src="/Nova_Logo_RGB_Earth_Steel_Pos_72ppi-01.png"
                            style={{
                                width: 154,
                                height: 61,
                            }}
                            alt=""
                        />
                    </Link>

                    <div className={classes.right}>
                        <Typography
                            style={{
                                display: 'grid',
                                placeItems: 'center',
                                gridAutoFlow: 'column',
                            }}
                        >
                            <Link className={classes.link}>
                                <Person className={classes.icon} />{' '}
                                {loginStatus}
                            </Link>
                            <Link
                                className={classes.link}
                                component="button"
                                onClick={handleMenuClick}
                            >
                                <MenuIcon className={classes.icon} />
                                Menu
                            </Link>
                            <Menu
                                anchorEl={anchorEl}
                                keepMounted
                                open={Boolean(anchorEl)}
                                onClose={handleMenuClose}
                                // getContentAnchorEl={null}
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'left',
                                }}
                            >
                                <MenuItem onClick={handleMyDetailsClick}>
                                    Profile
                                </MenuItem>

                                {user.object?.isStaff && (
                                    <MenuItem onClick={handleAdminPageClick}>
                                        Admin
                                    </MenuItem>
                                )}

                                <MenuItem onClick={handleAPIPageClick}>
                                    View API
                                </MenuItem>

                                <MenuItem onClick={onLogoutClick}>
                                    Logout
                                </MenuItem>
                            </Menu>
                        </Typography>
                    </div>
                </Toolbar>
            </AppBar>
        </div>
    );
};

Navbar.displayName = 'Navbar';
export default Navbar;
